import type { Option } from "../core/option.ts";
import type { SheetId } from "./sheet-id.ts";

/**
 * A sheet key as a route spells it (`/api/sheets/{sheetKey}`; the server's
 * `UserScopedSheetKey`: `ss_<hex>`, a raw UUID, `:lookup` or
 * `[user]:lookup`): a string, but only `SheetKey.toNetworkKey` (a key the
 * client holds), `SheetNetworkKey.of` (a `SheetId`) or `parse` / `fromWire`
 * (a key already in its wire spelling) make one, so the place a sheet
 * identity becomes route text is chosen at the call, not left to whoever
 * held a string.
 */
export type SheetNetworkKey = string & { readonly __brand: "SheetNetworkKey" };

/** Route spellings of a sheet key: `of`, `parse`, `fromWire`. */
export namespace SheetNetworkKey {
  /** `id` as a route spells it: its canonical form (`ss_<hex>`), verbatim. */
  export function of(id: SheetId): SheetNetworkKey {
    return mint(id.toString());
  }

  /**
   * `raw` as a route key, or `null` when it is not one (an empty string) —
   * for a key that arrives in its wire spelling (a response's field, a
   * command-line argument) and is sent as is.
   */
  export function parse(raw: string): Option<SheetNetworkKey> {
    return raw.length > 0 ? mint(raw) : null;
  }

  /**
   * `raw`, a sheet key the SDK holds in its wire spelling, sent verbatim
   * (no canonicalization: a raw UUID stays a raw UUID); throws when `parse`
   * refuses it (an empty string).
   */
  export function fromWire(raw: string): SheetNetworkKey {
    const key = parse(raw);
    if (key == null) throw new Error("SheetNetworkKey.fromWire: an empty sheet key");
    return key;
  }
}

/** The brand's one mint: every constructor above decides what the text is. */
function mint(raw: string): SheetNetworkKey {
  return raw as SheetNetworkKey;
}
